import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import { isDevelopment } from '~/utils/misc'
import { useMediaQuery } from 'usehooks-ts'

import Breadcrumbs from '~/components/UI/General/Breadcrumbs/Breadcrumbs'
import MarkDown from '~/components/Helpers/MarkDown'
import IntroText from '~/components/Text/IntroText/IntroText'
import BlockVideo from '~/components/Media/components/BlockVideo'
import Badge from '~/components/UI/General/Badge/Badge'
import PrimaryCTA from '~/components/UI/Buttons/PrimaryCTA/PrimaryCTA'
import SecondaryCTA from '~/components/UI/Buttons/SecondaryCTA/SecondaryCTA'
import SearchAutocomplete from '~/components/Content/SearchAutocomplete/SearchAutocomplete'
import { useSearchAutocomplete } from '~/hooks/useAutocompleteSearch'

const heroVariants = cva('', {
  variants: {
    backgroundColour: {
      'ink-blue': 'bg-blue-d text-white',
      'mid-blue': 'bg-blue-m text-white',
      chartreuse: 'bg-char text-blue-d',
      'light-grey': 'bg-grey-l text-blue-d',
      white: 'bg-white text-blue-d'
    },
    overlayOpacity: {
      '80': 'bg-black/80',
      '60': 'bg-black/60',
      '40': 'bg-black/40',
      '20': 'bg-black/20',
      '0': ''
    }
  },
  defaultVariants: {
    backgroundColour: 'ink-blue',
    overlayOpacity: '60'
  }
})

const HeroBespokeV1 = ({ data, breadcrumb }: HeroProps) => {
  const isDesktop = useMediaQuery('(min-width: 1280px)', {
    defaultValue: true,
    initializeWithValue: false
  })

  const {
    heading,
    eyebrow,
    backgroundColour,
    userAction,
    introParagraph,
    image,
    video,
    badge,
    search,
    urbanTexture,
    technicalTexture,
    overlayOpacity
  } = data

  const primaryButton = data?.buttons[0] || null
  const secondaryButton = data?.buttons[1] || null

  const foregroundColour =
    backgroundColour === 'ink-blue' || backgroundColour === 'mid-blue'
      ? 'white'
      : 'ink-blue'

  const previewUrl =
    'https://preview-websitemodern-uni-hull.cloud.contensis.com'

  // Updated search prop to match SearchItem[] type
  const updatedSearch = search?.map(item => ({
    ...item,
    contentType: Array.isArray(item.contentType)
      ? item.contentType
      : Array.isArray(item.contentType?.id)
        ? item.contentType.id.map(id => ({ id }))
        : []
  }))

  const { mappedItems, isSuccess, handleQueryChange } =
    useSearchAutocomplete(updatedSearch)

  return (
    <section className='relative flex flex-col items-center xl:min-h-[750px] xl:flex-row'>
      {badge && badge.length > 0 && (
        <Badge
          className='sm:top-m top-s xl:top-l absolute right-0 z-1'
          data={badge[0]}
        />
      )}
      <div className='relative aspect-3/2 h-full w-full sm:aspect-2/1 xl:absolute xl:aspect-4/3'>
        {video && image ? (
          <BlockVideo
            videoUrl={video?.sys.uri}
            width={''}
            height={''}
            image={image}
            btnClassName='!bottom-xl lg:!bottom-2xl w-[36px] h-[36px] !p-2xs hover:bg-grey-l transition-colors'
          />
        ) : (
          image && (
            <img
              className='h-full w-full object-cover'
              src={`${
                isDevelopment && !image?.asset.sys.uri.startsWith('https')
                  ? previewUrl
                  : ''
              }${image?.asset.sys.uri}`}
              alt={image?.altText}
            />
          )
        )}

        <div
          className='h-m absolute bottom-0 left-0 z-1 w-full xl:h-[40px]'
          style={{
            backgroundImage: `url(${
              isDevelopment && !urbanTexture?.asset.sys.uri.startsWith('https')
                ? previewUrl
                : ''
            }${urbanTexture?.asset.sys.uri}?w=1000&h=40&fit=crop&q=75&f=webp)`,
            backgroundSize: 'cover',
            opacity: 1
          }}
        ></div>
        <div
          className={cn(
            'xl:bottom-xs-plus bottom-xs h-m absolute left-0 z-1 w-full mix-blend-hard-light xl:h-[40px]'
          )}
          style={{
            backgroundImage: `url(${
              isDevelopment &&
              !technicalTexture?.asset.sys.uri.startsWith('https')
                ? previewUrl
                : ''
            }${technicalTexture?.asset.sys.uri}?w=1000&h=40&fit=crop&q=75&f=webp)`,
            backgroundSize: 'cover',
            opacity: 1
          }}
        ></div>
      </div>
      <div
        className={cn(
          heroVariants({
            backgroundColour: null,
            overlayOpacity: overlayOpacity || '60'
          }),
          'absolute hidden h-full w-full xl:block'
        )}
      ></div>

      <div className='xl:px-2xl w-full'>
        <div className='relative z-1 flex w-full max-w-[1400px] flex-col xl:mx-auto xl:flex-row'>
          <div
            className={cn(
              heroVariants({
                backgroundColour: backgroundColour,
                overlayOpacity: null
              }),
              'sm:p-2xl px-xs-plus py-m space-y-s sm:space-y-m xl:space-y-l xl:mb-3xl xl:mt-2xl relative grow xl:bg-transparent xl:p-0 xl:text-white'
            )}
          >
            <Breadcrumbs
              data={breadcrumb}
              colour={isDesktop ? 'white' : foregroundColour}
            />
            <div className='space-y-xs xl:space-y-xs-plus'>
              {eyebrow && <div className='tag'>{eyebrow}</div>}
              <MarkDown content={'# ' + heading} />
            </div>

            <div className='space-y-l'>
              {introParagraph && introParagraph.length > 0 && (
                <IntroText
                  content={introParagraph[0]}
                  layoutColour={isDesktop ? 'white' : backgroundColour}
                />
              )}
              {userAction === 'buttons' && primaryButton && (
                <div className='gap-2xs-plus xl:gap-s flex flex-wrap'>
                  <PrimaryCTA
                    data={primaryButton}
                    colour={isDesktop ? 'white' : foregroundColour}
                  />
                  {secondaryButton && (
                    <SecondaryCTA
                      data={secondaryButton}
                      colour={isDesktop ? 'white' : foregroundColour}
                    />
                  )}
                </div>
              )}
              {userAction === 'search' && search && search.length > 0 && (
                <SearchAutocomplete
                  placeholder={search[0]?.placeholder || 'Search...'}
                  data={{
                    label: '',
                    note: ''
                  }}
                  loading={isSuccess}
                  items={mappedItems}
                  onQueryChange={handleQueryChange}
                  disabled={false}
                  className='text-blue-d max-w-[80ch]'
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default HeroBespokeV1
